interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  noIndex?: boolean;
  image?: string;
  jsonLd?: object | object[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  noIndex = false,
  image,
  jsonLd,
}: SEOProps) {
  const siteUrl =
    (
      import.meta.env.VITE_SITE_URL ??
      'https://www.safelinesolutions.net'
    ).replace(/\/$/, '');

  const resolvedCanonical = canonical
    ? canonical.startsWith('http')
      ? canonical
      : `${siteUrl}${canonical}`
    : undefined;

  const resolvedImage = image
    ? image.startsWith('http')
      ? image
      : `${siteUrl}${image}`
    : `${siteUrl}/og-cover.jpg`;

  const today = new Date()
    .toISOString()
    .split('T')[0];

  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <>
      {/* BASIC SEO */}
      <title>{title}</title>

      <meta
        name="description"
        content={description}
      />

      {keywords && (
        <meta
          name="keywords"
          content={keywords}
        />
      )}

      {resolvedCanonical && (
        <link
          rel="canonical"
          href={resolvedCanonical}
        />
      )}

      <meta
        name="last-modified"
        content={today}
      />

      {noIndex ? (
        <meta
          name="robots"
          content="noindex, nofollow"
        />
      ) : (
        <meta
          name="robots"
          content="index, follow"
        />
      )}

      {/* OPEN GRAPH */}
      <meta
        property="og:type"
        content="website"
      />

      <meta
        property="og:site_name"
        content="SafeLine Solutions"
      />

      <meta
        property="og:title"
        content={title}
      />

      <meta
        property="og:description"
        content={description}
      />

      {resolvedCanonical && (
        <meta
          property="og:url"
          content={resolvedCanonical}
        />
      )}

      <meta
        property="og:image"
        content={resolvedImage}
      />

      <meta
        property="og:image:secure_url"
        content={resolvedImage}
      />

      <meta
        property="og:image:type"
        content="image/jpeg"
      />

      <meta
        property="og:locale"
        content="es_CL"
      />

      {/* TWITTER / X */}
      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content={title}
      />

      <meta
        name="twitter:description"
        content={description}
      />

      <meta
        name="twitter:image"
        content={resolvedImage}
      />

      {/* THEME */}
      <meta
        name="theme-color"
        content="#07111A"
      />

      {/* JSON-LD */}
      {jsonLdArray.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      ))}
    </>
  );
}